import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Users, ArrowRight } from 'lucide-react';
import type { ParcoursIndexItem } from '../../types/parcours';

interface Props {
  parcours: ParcoursIndexItem;
}

export default function CourseCard({ parcours }: Props) {
  const href = `/decouvrir/${parcours.slug}`;

  return (
    <Link
      to={href}
      className="group flex flex-col rounded-2xl border border-neutral-100 bg-white overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
    >
      {/* Cover */}
      <div className="relative h-44 bg-gradient-to-br from-[#fdf2f8] to-[#fef6ee] overflow-hidden">
        {parcours.company_cover ? (
          <img
            src={parcours.company_cover}
            alt={parcours.company_name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-5xl font-bold text-[#d03484]/10">
              {parcours.company_name.charAt(0)}
            </span>
          </div>
        )}
        {/* Logo flottant */}
        {parcours.company_logo && (
          <div className="absolute bottom-3 left-4">
            <div className="h-10 w-10 rounded-lg bg-white shadow-sm p-1.5 overflow-hidden">
              <img
                src={parcours.company_logo}
                alt=""
                className="w-full h-full object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        )}
        {parcours.featured && (
          <div className="absolute top-3 right-3">
            <span className="rounded-full bg-[#d03484] px-2.5 py-0.5 text-xs font-semibold text-white">
              En avant
            </span>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-5">
        <h3 className="font-semibold text-neutral-900 text-lg leading-snug mb-1 group-hover:text-[#d03484] transition-colors">
          {parcours.company_name}
        </h3>
        {parcours.tagline && (
          <p className="text-sm text-neutral-500 mb-3 line-clamp-2">{parcours.tagline}</p>
        )}

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-400 mt-auto mb-4">
          {parcours.location && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {parcours.location}
            </span>
          )}
          {parcours.employee_count && (
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              {parcours.employee_count}
            </span>
          )}
          {parcours.sector && (
            <span className="rounded-full bg-neutral-100 px-2 py-0.5">{parcours.sector}</span>
          )}
        </div>

        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#d03484] group-hover:gap-2.5 transition-all">
          Découvrir l'entreprise
          <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
}
